import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Ruler, Blinds, Square, LayoutGrid, FileDown, Save, Trash2, Factory, Loader2, ClipboardList, SlidersHorizontal, Scroll, PaintBucket, Trees, Volume2, Rows3, Sofa, Box, Frame, Lightbulb, Package, Plus } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { num, money, exportPDF, Field, S_DEFAULT, ease } from "./calc/shared";
import Cortinas from "./calc/Cortinas";
import Persianas from "./calc/Persianas";
import Cojines from "./calc/Cojines";
import Recubrimientos from "./calc/Recubrimientos";
import PapelTapiz from "./calc/PapelTapiz";
import Pintura from "./calc/Pintura";
import Alfombras from "./calc/Alfombras";
import PastoSintetico from "./calc/PastoSintetico";
import PanelesAcusticos from "./calc/PanelesAcusticos";
import Deck from "./calc/Deck";
import Tapiceria from "./calc/Tapiceria";
import HuleEspuma from "./calc/HuleEspuma";
import Molduras from "./calc/Molduras";
import Iluminacion from "./calc/Iluminacion";

const TABS = [
  { key: "cortinas", label: "Cortinas", icon: Ruler, C: Cortinas },
  { key: "persianas", label: "Persianas", icon: Blinds, C: Persianas },
  { key: "cojines", label: "Cojines", icon: Square, C: Cojines },
  { key: "recubrimientos", label: "Recubrimientos", icon: LayoutGrid, C: Recubrimientos },
  { key: "papel_tapiz", label: "Papel Tapiz", icon: Scroll, C: PapelTapiz },
  { key: "pintura", label: "Pintura", icon: PaintBucket, C: Pintura },
  { key: "alfombras", label: "Alfombras", icon: Rows3, C: Alfombras },
  { key: "pasto_sintetico", label: "Pasto Sintético", icon: Trees, C: PastoSintetico },
  { key: "paneles_acusticos", label: "Paneles Acústicos", icon: Volume2, C: PanelesAcusticos },
  { key: "deck", label: "Deck", icon: Frame, C: Deck },
  { key: "tapiceria", label: "Tapicería", icon: Sofa, C: Tapiceria },
  { key: "hule_espuma", label: "Hule Espuma", icon: Box, C: HuleEspuma },
  { key: "molduras", label: "Molduras/Perfiles", icon: LayoutGrid, C: Molduras },
  { key: "iluminacion", label: "Iluminación", icon: Lightbulb, C: Iluminacion },
];
const MODALITY_LABEL = {
  cortinas: "Cortinas", persianas: "Persianas", cojines: "Cojines", recubrimientos: "Recubrimientos",
  papel_tapiz: "Papel Tapiz", pintura: "Pintura", alfombras: "Alfombras", pasto_sintetico: "Pasto Sintético",
  paneles_acusticos: "Paneles Acústicos", deck: "Deck", tapiceria: "Tapicería", hule_espuma: "Hule Espuma",
  molduras: "Molduras/Perfiles", iluminacion: "Iluminación",
};
const ORDER_STATUS = { borrador: "Borrador", produccion: "En producción", terminado: "Terminado", cancelado: "Cancelado" };
const STATUS_COLOR = { borrador: "bg-navy/10 text-navy/60", produccion: "bg-amber-100 text-amber-700", terminado: "bg-emerald-100 text-emerald-700", cancelado: "bg-red-100 text-red-600" };

const SETTINGS_FIELDS = [
  ["desperdicio_default", "Desperdicio por defecto (%)"],
  ["venta_min_tela", "Venta mínima tela (m)"],
  ["venta_min_area", "Venta mínima área (m²)"],
  ["precio_tela_m", "Precio tela ($/m)"],
  ["precio_confeccion", "Confección ($/pieza)"],
  ["precio_instalacion", "Instalación ($)"],
  ["precio_persiana_m2", "Precio persiana ($/m²)"],
  ["precio_recubrimiento_m2", "Precio recubrimiento ($/m²)"],
  ["margen_pct", "Margen (%)"],
  ["iva_pct", "IVA (%)"],
  ["carros_cada_cm", "Carros/ganchos cada (cm)"],
  ["ojillos_cada_cm", "Ojillos cada (cm)"],
  ["adhesivo_rend", "Rendimiento adhesivo (m²/saco)"],
];

function AdminSettings({ settings, onSave }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings); }, [settings]);
  const save = async () => {
    setSaving(true);
    const payload = {};
    SETTINGS_FIELDS.forEach(([k]) => { payload[k] = num(form[k]); });
    try { await onSave(payload); } finally { setSaving(false); }
  };
  return (
    <div className="card-surface p-5" data-testid="calc-admin-settings">
      <div className="flex items-center gap-2 overline text-navy/40 mb-4"><SlidersHorizontal className="w-3.5 h-3.5" /> Valores por defecto (administración)</div>
      <p className="text-sm text-navy/50 mb-4">Estos valores precargan los campos de la calculadora para todas las cotizaciones nuevas de tu empresa.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SETTINGS_FIELDS.map(([k, label]) => (
          <Field key={k} label={label} value={form[k] ?? ""} onChange={(v) => setForm((f) => ({ ...f, [k]: v }))} testid={`set-${k}`} />
        ))}
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={saving} className="btn bg-gradient-violet text-white text-sm" data-testid="calc-settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar ajustes
        </button>
      </div>
    </div>
  );
}

const CATALOG_UNITS = ["pieza", "rollo", "m", "m²", "caja", "litro", "barra", "placa", "saco", "luminaria"];

function CatalogManager() {
  const [modality, setModality] = useState("cortinas");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", price: "", unit: "pieza", stock: "" });
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get(`/gout/calculator/catalog?modality=${modality}`).then((r) => setItems(r.data)).catch(() => {}), [modality]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    const body = { name: form.name.trim(), price: num(form.price), unit: form.unit, stock: num(form.stock) };
    try {
      if (editing) await api.patch(`/gout/calculator/catalog/${editing}`, body);
      else await api.post("/gout/calculator/catalog", { modality, specs: {}, ...body });
      setForm({ name: "", price: "", unit: "pieza", stock: "" }); setEditing(null);
      await load(); toast.success("Producto guardado");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/gout/calculator/catalog/${id}`); await load(); toast.success("Producto eliminado"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="card-surface p-5 space-y-4" data-testid="calc-catalog-manager">
      <div className="flex items-center gap-2 overline text-navy/40"><Package className="w-3.5 h-3.5" /> Catálogo de productos e inventario</div>
      <p className="text-sm text-navy/50">Los productos aparecen como opción en cada calculadora, precargan el precio y validan el inventario disponible.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <Field label="Modalidad">
          <select value={modality} onChange={(e) => { setModality(e.target.value); setEditing(null); }} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5" data-testid="catalog-modality">
            {TABS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Nombre del producto" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} type="text" step={undefined} min={undefined} testid="catalog-name" />
        <Field label="Precio" value={form.price} onChange={(v) => setForm((f) => ({ ...f, price: v }))} suffix="$" testid="catalog-price" />
        <Field label="Unidad">
          <select value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5" data-testid="catalog-unit">
            {CATALOG_UNITS.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Field label="Stock (inventario)" value={form.stock} onChange={(v) => setForm((f) => ({ ...f, stock: v }))} testid="catalog-stock" />
      </div>
      <div className="flex justify-end gap-2">
        {editing && <button onClick={() => { setEditing(null); setForm({ name: "", price: "", unit: "pieza", stock: "" }); }} className="btn btn-ghost text-sm">Cancelar</button>}
        <button onClick={submit} className="btn bg-gradient-violet text-white text-sm" data-testid="catalog-submit"><Plus className="w-4 h-4" /> {editing ? "Actualizar" : "Agregar"} producto</button>
      </div>
      <div className="space-y-1.5">
        {items.length === 0 ? <p className="text-sm text-navy/40">Sin productos en esta modalidad.</p> : items.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 border-b border-navy/5 last:border-0 py-2" data-testid={`catalog-item-${p.id}`}>
            <div className="text-sm">{p.name} <span className="text-navy/40">· {money(p.price)}/{p.unit} · stock {p.stock}</span></div>
            <div className="flex gap-1.5">
              <button onClick={() => { setEditing(p.id); setForm({ name: p.name, price: String(p.price), unit: p.unit, stock: String(p.stock) }); }} className="btn btn-ghost text-xs" data-testid={`catalog-edit-${p.id}`}>Editar</button>
              <button onClick={() => remove(p.id)} className="btn btn-ghost text-xs text-destructive" data-testid={`catalog-del-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InteriorCalculator() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("cortinas");
  const [panel, setPanel] = useState(null); // 'saved' | 'orders' | 'settings' | 'catalog'
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]);
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState(S_DEFAULT);
  const [ready, setReady] = useState(false);

  const loadProjects = useCallback(() => api.get("/gout/calculator/projects").then((r) => setProjects(r.data)).catch(() => {}), []);
  const loadOrders = useCallback(() => api.get("/gout/calculator/orders").then((r) => setOrders(r.data)).catch(() => {}), []);

  useEffect(() => {
    Promise.all([
      api.get("/gout/calculator/settings").then((r) => setSettings({ ...S_DEFAULT, ...r.data })).catch(() => {}),
      api.get("/gout/calculator/members").then((r) => setMembers(r.data)).catch(() => {}),
      loadProjects(),
      loadOrders(),
    ]).finally(() => setReady(true));
  }, [loadProjects, loadOrders]);

  const save = async (payload) => {
    try { await api.post("/gout/calculator/projects", payload); await loadProjects(); toast.success("Cotización guardada"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/gout/calculator/projects/${id}`); await loadProjects(); toast.success("Cotización eliminada"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const toOrder = async (id) => {
    try {
      const { data } = await api.post(`/gout/calculator/projects/${id}/to-order`);
      await Promise.all([loadProjects(), loadOrders()]);
      toast.success(`Orden de fabricación ${data.folio} generada`);
      setPanel("orders");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const setOrderStatus = async (id, status) => {
    try { await api.patch(`/gout/calculator/orders/${id}`, { status }); await loadOrders(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const delOrder = async (id) => {
    try { await api.delete(`/gout/calculator/orders/${id}`); await Promise.all([loadOrders(), loadProjects()]); toast.success("Orden eliminada"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const assignOrder = async (id, assignee_id) => {
    try {
      await api.patch(`/gout/calculator/orders/${id}`, { assignee_id });
      await loadOrders();
      toast.success("Responsable asignado y notificado");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const downloadOrderPDF = async (o) => {
    try {
      const res = await api.get(`/gout/calculator/orders/${o.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `OF-${o.folio}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo generar el PDF"); }
  };
  const saveSettings = async (payload) => {
    try { const { data } = await api.put("/gout/calculator/settings", payload); setSettings({ ...S_DEFAULT, ...data }); toast.success("Ajustes guardados"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const Active = TABS.find((t) => t.key === tab).C;

  return (
    <OSLayout title="GOÛT · Calculadora de Interiores">
      <div className="space-y-8" data-testid="interior-calculator">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <Link to="/os/gout" className="inline-flex items-center gap-1.5 text-sm text-navy/50 hover:text-navy transition-colors mb-3" data-testid="calc-back">
            <ArrowLeft className="w-4 h-4" /> GOÛT Studio
          </Link>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="overline text-navy/40 mb-2">GOÛT · Design Intelligence Studio</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Calculadora de Interiores</h2>
              <p className="mt-2 text-navy/50 max-w-2xl">Cotizador profesional de 14 modalidades (cortinas, persianas, cojines, recubrimientos, papel tapiz, pintura, alfombras, pasto sintético, paneles acústicos, deck, tapicería, hule espuma, molduras e iluminación): unidades, accesorios, optimización de corte, catálogo con inventario, costos, PDF y órdenes de fabricación.</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setPanel(panel === "saved" ? null : "saved")} className={`btn text-sm ${panel === "saved" ? "bg-gradient-violet text-white" : "btn-outline"}`} data-testid="calc-projects-toggle">
                <ClipboardList className="w-4 h-4" /> Cotizaciones ({projects.length})
              </button>
              <button onClick={() => setPanel(panel === "orders" ? null : "orders")} className={`btn text-sm ${panel === "orders" ? "bg-gradient-violet text-white" : "btn-outline"}`} data-testid="calc-orders-toggle">
                <Factory className="w-4 h-4" /> Órdenes ({orders.length})
              </button>
              {isAdmin && (
                <button onClick={() => setPanel(panel === "settings" ? null : "settings")} className={`btn text-sm ${panel === "settings" ? "bg-gradient-violet text-white" : "btn-outline"}`} data-testid="calc-settings-toggle">
                  <SlidersHorizontal className="w-4 h-4" /> Ajustes
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setPanel(panel === "catalog" ? null : "catalog")} className={`btn text-sm ${panel === "catalog" ? "bg-gradient-violet text-white" : "btn-outline"}`} data-testid="calc-catalog-toggle">
                  <Package className="w-4 h-4" /> Catálogo
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {panel === "saved" && (
          <div className="card-surface p-4" data-testid="calc-projects">
            {projects.length === 0 ? <p className="text-sm text-navy/40">Aún no has guardado cotizaciones. Usa "Guardar" en el resultado.</p> : (
              <div className="space-y-2">
                {projects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 border-b border-navy/5 last:border-0 py-2.5" data-testid={`calc-project-${p.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm truncate flex items-center gap-2">{p.title}
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet/10 text-violet">{MODALITY_LABEL[p.modality] || p.modality}</span>
                        {p.order_id && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Con orden</span>}
                      </div>
                      <div className="text-[11px] text-navy/40">{p.client ? `${p.client} · ` : ""}{money(p.total || 0)} · {new Date(p.created_at).toLocaleString("es-MX")}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => exportPDF(p.title, p.rows)} className="btn btn-ghost text-xs" title="PDF"><FileDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => toOrder(p.id)} disabled={!!p.order_id} className="btn btn-ghost text-xs disabled:opacity-30" title="Generar orden de fabricación" data-testid={`calc-toorder-${p.id}`}><Factory className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(p.id)} className="btn btn-ghost text-xs text-destructive" title="Eliminar" data-testid={`calc-delproject-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {panel === "orders" && (
          <div className="card-surface p-4" data-testid="calc-orders">
            {orders.length === 0 ? <p className="text-sm text-navy/40">Sin órdenes de fabricación. Genera una desde una cotización guardada.</p> : (
              <div className="space-y-2">
                {orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-3 border-b border-navy/5 last:border-0 py-2.5" data-testid={`calc-order-${o.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm truncate flex items-center gap-2">
                        <span className="font-mono text-xs text-navy/50">{o.folio}</span> {o.title}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[o.status] || ""}`}>{ORDER_STATUS[o.status] || o.status}</span>
                      </div>
                      <div className="text-[11px] text-navy/40">{MODALITY_LABEL[o.modality] || o.modality}{o.client ? ` · ${o.client}` : ""} · {o.materials?.length || 0} partidas{o.assignee_name ? ` · 👤 ${o.assignee_name}` : ""}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0 items-center">
                      <select value={o.assignee_id || ""} onChange={(e) => e.target.value && assignOrder(o.id, e.target.value)} className="bg-white border border-navy/10 rounded-lg px-2 py-1.5 text-xs max-w-[10rem]" data-testid={`calc-order-assignee-${o.id}`}>
                        <option value="">Asignar responsable…</option>
                        {members.map((m) => <option key={m.id} value={m.id}>{m.name || m.email}</option>)}
                      </select>
                      <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)} className="bg-white border border-navy/10 rounded-lg px-2 py-1.5 text-xs" data-testid={`calc-order-status-${o.id}`}>
                        {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <button onClick={() => downloadOrderPDF(o)} className="btn btn-ghost text-xs" title="PDF de fabricación" data-testid={`calc-order-pdf-${o.id}`}><FileDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => delOrder(o.id)} className="btn btn-ghost text-xs text-destructive" title="Eliminar" data-testid={`calc-delorder-${o.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {panel === "settings" && isAdmin && <AdminSettings settings={settings} onSave={saveSettings} />}
        {panel === "catalog" && isAdmin && <CatalogManager />}

        <div className="flex gap-2 flex-wrap">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`calc-tab-${key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm border transition-colors ${tab === key ? "bg-gradient-violet text-white border-transparent" : "bg-white border-navy/15 text-navy/60 hover:border-navy/40"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {ready && (
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}>
            <Active onSave={save} s={settings} />
          </motion.div>
        )}
      </div>
    </OSLayout>
  );
}
